/**
 * Support Guide: Managing Attempts as a Producer
 * Category: OBS & Broadcast (green #22c55e)
 *
 * 720 frames @ 30fps = 24 seconds
 *
 * Timeline:
 *   0-55     Title card
 *   55-200   Producer: scores on cards, show attempt controls
 *   200-300  Producer: + pulses, Harley advances 2→3
 *   300-440  OBS FULL SCREEN: Harley on 2 dots… wait… bumps to 3 dots
 *   440-580  Judge FULL SCREEN: Harley on attempt 2… wait… attempt 3 unlocks yellow
 *   580-640  Producer: advance Nic Rapa 1→2
 *   640-720  End scene
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { SlideIn } from "../../components/SlideIn";
import { TypeWriter } from "../../components/TypeWriter";
import { StepLabel } from "../../components/StepLabel";
import { ProducerScreen, type ProducerRider } from "../screens/ProducerScreen";
import { JudgeScreen, type JudgeRider } from "../screens/JudgeScreen";
import { OverlayScreen, type OverlayRider } from "../screens/OverlayScreen";

const ACCENT = "#22c55e";
const C = {
  bg: "#0a0a0b", accent: "#3b82f6", danger: "#ef4444",
  success: "#22c55e", warning: "#eab308",
  text: "#e4e4e7", textDim: "#52525b",
};
const F = { sans: "'Inter', sans-serif", mono: "'JetBrains Mono', monospace" };

export function ManageAttemptsGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: C.bg, fontFamily: F.sans }}>
      <Sequence durationInFrames={55}>
        <TitleCard />
      </Sequence>
      <Sequence from={55} durationInFrames={665}>
        <Walkthrough />
      </Sequence>
    </AbsoluteFill>
  );
}

function TitleCard() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: F.mono, marginBottom: 16 }}>
          Support Guide
        </div>
        <div style={{ fontSize: 48, fontWeight: 800, color: "#fff", maxWidth: 900, lineHeight: 1.1 }}>
          Managing Attempts as a Producer
        </div>
        <div style={{ marginTop: 20 }}>
          <TypeWriter text="OBS & Broadcast" delay={10} speed={1} style={{ fontSize: 16, color: "#94a3b8" }} />
        </div>
      </div>
    </AbsoluteFill>
  );
}

function Walkthrough() {
  const frame = useCurrentFrame();

  // ── Key moments ──
  const harleyAdvances = 280;
  const nicAdvances = 610;
  const obsBumpsAt = 390;   // OBS view: dots go 2→3 here (90 frames into OBS view)
  const judgeBumpsAt = 520;  // Judge view: attempt 3 unlocks here (80 frames into Judge view)

  const harleyAdvanced = frame >= harleyAdvances;
  const nicAdvanced = frame >= nicAdvances;

  // ── Producer riders (with judge scores matching real app screenshots) ──
  const producerRiders: ProducerRider[] = [
    {
      name: "Harley Clifford", nickname: "The King", num: "1", colour: C.accent, focused: false,
      attempts: [8.5, 7.9, harleyAdvanced && frame >= 620 ? 8.4 : null],
      current: harleyAdvanced ? 3 : 2,
      judgeScores: { Sarah: [8.0, 7.5, null], Mike: [9.0, 8.5, null], Lily: [8.5, 7.7, null] },
    },
    {
      name: "Nic Rapa", nickname: "Raptor", num: "7", colour: C.success, focused: false,
      attempts: [7.2, nicAdvanced ? null : null, null],
      current: nicAdvanced ? 2 : 1,
      judgeScores: { Sarah: [7.0, null, null], Mike: [7.5, null, null], Lily: [7.1, null, null] },
    },
    {
      name: "Dallas Friday", nickname: "D-Fri", num: "12", colour: C.warning, focused: false,
      attempts: [8.4, null, null], current: 1,
      judgeScores: { Sarah: [8.5, null, null], Mike: [8.5, null, null], Lily: [8.2, null, null] },
    },
    {
      name: "Amber Wing", nickname: "Wings", num: "3", colour: C.danger, focused: false,
      attempts: [6.5, 7.1, null], current: 2,
      judgeScores: { Sarah: [6.5, 7.0, null], Mike: [6.5, 7.5, null], Lily: [6.5, 6.8, null] },
    },
    {
      name: "Steel Lafferty", nickname: "Steele", num: "22", colour: "#a855f7", focused: false,
      attempts: [8.1, null, null], current: 1,
      judgeScores: { Sarah: [8.0, null, null], Mike: [8.5, null, null], Lily: [7.8, null, null] },
    },
    {
      name: "Meagan Ethell", nickname: "Mega", num: "5", colour: "#f97316", focused: false,
      attempts: [8.0, 8.5, null], current: 2,
      judgeScores: { Sarah: [8.0, 8.5, null], Mike: [8.0, 8.5, null], Lily: [8.0, 8.5, null] },
    },
  ];

  // ── OBS overlay riders: Harley's currentAttempt transitions 2→3 at obsBumpsAt ──
  const obsBumped = frame >= obsBumpsAt;
  const overlayRiders: OverlayRider[] = [
    { name: "Harley Clifford", nick: "The King", initials: "HC", colour: "#3b82f6", attempts: [8.7, 9.1, null], final: 9.1, currentAttempt: obsBumped ? 3 : 2, maxAttempts: 3 },
    { name: "Nic Rapa", nick: "Raptor", initials: "NR", colour: "#22c55e", attempts: [7.2, null, null], final: 7.2, currentAttempt: 1, maxAttempts: 3 },
    { name: "Dallas Friday", nick: "D-Fri", initials: "DF", colour: "#eab308", attempts: [8.4, null, null], final: 8.4, currentAttempt: 1, maxAttempts: 3 },
    { name: "Amber Wing", nick: "Wings", initials: "AW", colour: "#ef4444", attempts: [6.5, 7.1, null], final: 7.1, currentAttempt: 2, maxAttempts: 3 },
    { name: "Steel Lafferty", nick: "Steele", initials: "SL", colour: "#a855f7", attempts: [8.1, null, null], final: 8.1, currentAttempt: 1, maxAttempts: 3 },
  ];

  // ── Judge riders: Harley's current transitions 2→3 at judgeBumpsAt ──
  const judgeBumped = frame >= judgeBumpsAt;
  const judgeRiders: JudgeRider[] = [
    { name: "Harley Clifford", nick: "The King", scores: { 1: 8.5, 2: 9.2 }, current: judgeBumped ? 3 : 2 },
    { name: "Nic Rapa", nick: "Raptor", scores: { 1: 7.0 }, current: 1 },
    { name: "Dallas Friday", nick: "D-Fri", scores: { 1: 9.0 }, current: 1 },
    { name: "Amber Wing", nick: "Wings", scores: { 1: 6.5, 2: 7.1 }, current: 2 },
    { name: "Steel Lafferty", nick: "Steele", scores: { 1: 8.1 }, current: 1 },
    { name: "Meagan Ethell", nick: "Mega", scores: { 1: 8.0, 2: 8.5 }, current: 2 },
    { name: "Cory Teunissen", nick: "CT", scores: { 1: 7.5 }, current: 1 },
    { name: "Anna Nikstad", nick: "Nikki", scores: { 1: 7.8, 2: 8.0 }, current: 3 },
    { name: "Bob Soven", nick: "Bobby", scores: {}, current: 1 },
    { name: "Tony Iacconi", nick: "Tone", scores: { 1: 8.5, 2: 9.0 }, current: 3 },
  ];

  // ── Pulse + button ──
  const pulseIdx = (frame >= 200 && frame < harleyAdvances) ? 0 : (frame >= 580 && frame < nicAdvances) ? 1 : undefined;

  // ── View switching ──
  const showOBS = frame >= 300 && frame < 440;
  const showJudge = frame >= 440 && frame < 580;
  const showEnd = frame >= 640;

  const obsOpacity = showOBS
    ? interpolate(frame, [300, 318, 422, 440], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
    : 0;
  const judgeOpacity = showJudge
    ? interpolate(frame, [440, 458, 562, 580], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
    : 0;
  const producerOpacity = (showOBS || showJudge) ? 1 - Math.max(obsOpacity, judgeOpacity) : showEnd ? 0 : 1;
  const endOpacity = showEnd ? interpolate(frame, [640, 660], [0, 1], { extrapolateRight: "clamp" }) : 0;

  return (
    <AbsoluteFill>
      {/* ── Producer screen ── */}
      <AbsoluteFill style={{ opacity: producerOpacity }}>
        <ProducerScreen riders={producerRiders} maxAttempts={3} pulseAdvanceIndex={pulseIdx} />
      </AbsoluteFill>

      {/* ── OBS FULL SCREEN — rider bar with dots + scores, Harley bumps 2→3 ── */}
      {showOBS && (
        <AbsoluteFill style={{ opacity: obsOpacity }}>
          <div style={{ width: 1920, height: 1080, position: "relative", background: "linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 50%, #0d1b2a 100%)" }}>
            <div style={{
              position: "absolute", top: 16, right: 24, zIndex: 10,
              display: "flex", alignItems: "center", gap: 8,
              background: "rgba(0,0,0,0.6)", padding: "4px 12px", borderRadius: 3,
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.danger, boxShadow: `0 0 6px ${C.danger}` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, fontFamily: F.mono }}>OBS Output</span>
            </div>
            <OverlayScreen showFocus={false} showScorecard={false} showLeaderboard={false} riders={overlayRiders} />
          </div>
        </AbsoluteFill>
      )}

      {/* ── Judge FULL SCREEN — Harley bumps 2→3, yellow highlight ── */}
      {showJudge && (
        <AbsoluteFill style={{ opacity: judgeOpacity }}>
          <JudgeScreen
            riders={judgeRiders}
            maxAttempts={3}
            highlightRider={judgeBumped ? "Harley Clifford" : undefined}
          />
        </AbsoluteFill>
      )}

      {/* ── Step labels (shared StepLabel — centre bottom) ── */}
      <StepLabel text="Each rider card shows per-attempt scores and attempt controls" show={0} hide={145} color={ACCENT} />
      <StepLabel text="Click + to advance a rider — click − to revert" show={145} hide={210} color={ACCENT} />
      <StepLabel text="Advance Harley Clifford from attempt 2 → 3" show={210} hide={305} color={ACCENT} />
      <StepLabel text="Watch the OBS rider bar — Harley is on 2 attempt dots…" show={305} hide={385} color={ACCENT} />
      <StepLabel text="Harley now shows 3 filled dots — attempt 3 is active" show={385} hide={445} color={ACCENT} />
      <StepLabel text="Watch the Judge screen — Harley is on attempt 2…" show={445} hide={515} color={ACCENT} />
      <StepLabel text="Attempt 3 unlocks — judges can now score it" show={515} hide={585} color={ACCENT} />
      <StepLabel text="Advance Nic Rapa from attempt 1 → 2" show={585} hide={640} color={ACCENT} />

      {/* ── End scene ── */}
      {showEnd && (
        <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: endOpacity }}>
          <SlideIn>
            <div style={{ textAlign: "center", maxWidth: 700 }}>
              <div style={{ fontSize: 42, fontWeight: 800, color: ACCENT, marginBottom: 20 }}>
                You control the pace
              </div>
              <div style={{ fontSize: 18, color: "#94a3b8", lineHeight: 1.6, marginBottom: 32 }}>
                Judges cannot score a rider's next attempt until you advance it.
                <br />
                The OBS overlay updates instantly when you do.
              </div>
              <div style={{ display: "flex", gap: 32, justifyContent: "center", marginBottom: 32 }}>
                {[
                  { label: "Producer", desc: "Clicks + to advance", color: ACCENT },
                  { label: "OBS", desc: "Rider bar updates instantly", color: C.accent },
                  { label: "Judges", desc: "Next attempt unlocked to score", color: C.warning },
                ].map((item) => (
                  <div key={item.label} style={{ textAlign: "center", minWidth: 160 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: item.color, textTransform: "uppercase" as const, letterSpacing: 1, fontFamily: F.mono, marginBottom: 6 }}>{item.label}</div>
                    <div style={{ fontSize: 13, color: C.textDim, lineHeight: 1.4 }}>{item.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                background: "rgba(34,197,94,0.1)", border: `1px solid ${ACCENT}`,
                padding: "10px 24px", marginTop: 8,
              }}>
                <span style={{ fontSize: 15, color: ACCENT, fontFamily: F.mono, fontWeight: 700 }}>+</span>
                <span style={{ fontSize: 14, color: C.text }}>→ OBS updates → Judges can score</span>
              </div>
            </div>
          </SlideIn>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
}
